import { useRef } from 'react';

import { LearnMoreSection } from '#/components/detail/LearnMoreSection';
import { categoryContentNavSections } from '#/components/index/categoryNavSections';
import { CategoryRelatedSection } from '#/components/index/CategoryRelatedSection';
import { EquitiesExploreTable } from '#/components/index/EquitiesExploreTable';
import { LearningSection } from '#/components/index/LearningSection';
import { ToolsResourcesSection } from '#/components/index/ToolsResourcesSection';
import type { CategoryIndexProps } from '#/components/index/types';
import { ContentLayout } from '#/components/layout/ContentLayout';
import { ArrowLink } from '#/components/ui/ArrowLink';
import { BrandAccent } from '#/components/ui/BrandAccent';
import { Breadcrumbs } from '#/components/ui/Breadcrumbs';
import { ErrorBoundary } from '#/components/ui/ErrorBoundary';
import { IconLabel } from '#/components/ui/IconLabel';
import { PageTitle } from '#/components/ui/PageTitle';
import { PricingDisclaimer } from '#/components/ui/PricingDisclaimer';
import { SectionHeader } from '#/components/ui/SectionHeader';
import { SectionNav } from '#/components/ui/SectionNav';
import { useCategoryContent } from '#/content/category';
import { useBreadcrumbs } from '#/hooks/useBreadcrumbs';
import {
  getCategoryBreadcrumbLabel,
  getCategoryIconLabel,
  getRelatedCategoryTopics,
} from '#/lib/category-navigation';

import { RichText } from '../ui/RichText';

export function EquitiesIndex({ category }: CategoryIndexProps) {
  const contentRef = useRef<HTMLDivElement>(null);
  const { content } = useCategoryContent(category);

  // The category crumb from the layout IS this page — slice it off.
  const breadcrumbs = useBreadcrumbs().slice(0, -1);
  const categoryLabel = getCategoryBreadcrumbLabel(category);
  const relatedTopics = getRelatedCategoryTopics(category);

  const sections = [
    ...categoryContentNavSections(content),
    { id: 'explore-equities', label: 'Explore Equities' },
    ...(relatedTopics.length > 0
      ? [{ id: 'related', label: 'Related Topics' }]
      : []),
  ];

  return (
    <ContentLayout>
      <SectionNav
        sections={sections}
        contentRef={contentRef}
        jumpTrack={{ track: 'section_nav_jump', context: 'section_nav' }}
      />

      <div className="col-content flow flow-60" ref={contentRef}>
        <section className="flow flow-16" id="summary">
          <Breadcrumbs
            items={breadcrumbs}
            dataTrack="breadcrumb_link"
            dataTrackContext="breadcrumb"
          />

          <BrandAccent variant="long" />

          <PageTitle>{content.pageTitle ?? categoryLabel}</PageTitle>

          <IconLabel label={getCategoryIconLabel(category)} />

          {content.intro && <RichText html={content.intro} />}
        </section>

        {content.inDepthLink && (
          <ArrowLink
            href={content.inDepthLink.href}
            className="flow-20"
            dataTrack="category_in_depth_link"
            dataTrackLabel={content.inDepthLink.title}
            dataTrackContext="category_index"
          >
            {content.inDepthLink.title}
          </ArrowLink>
        )}

        {content.relatedArticles && (
          <ErrorBoundary>
            <LearnMoreSection
              id="related-topics"
              articles={content.relatedArticles}
              articleTrack="learn_more_article"
              articleTrackContext="learn_more"
            />
          </ErrorBoundary>
        )}

        {content.learning && (
          <ErrorBoundary>
            <LearningSection content={content.learning} />
          </ErrorBoundary>
        )}

        {content.toolsResources && (
          <ErrorBoundary>
            <ToolsResourcesSection
              cards={content.toolsResources}
              cardTrack="tools_resource_card"
              cardTrackContext="tools_resources"
            />
          </ErrorBoundary>
        )}

        <ErrorBoundary>
          <EquitiesExploreTable
            tickerTrack={{
              track: 'explore_table_ticker',
              context: 'explore_table',
            }}
          >
            <SectionHeader title="Explore Equities" />
            <PricingDisclaimer />
          </EquitiesExploreTable>
        </ErrorBoundary>

        <ErrorBoundary>
          <CategoryRelatedSection
            topics={relatedTopics}
            topicTrack="related_topic_link"
            topicTrackContext="related_topics"
          />
        </ErrorBoundary>
      </div>
    </ContentLayout>
  );
}
